'use client';

/**
 * ProductDescriptionCard — long-form description prose ONLY.
 *
 * Slices `FullProductDetail.description_sale` (fallback `description`)
 * from the shared `graph_product_explore` payload. Structured facets
 * (dimensions, lead time, collection, vendor) live on
 * `product_specs` — per cards-everywhere SKILL.md anti-pattern #1, one
 * card per concept. Shares ONE network round-trip with sibling overlay
 * cards via React Query dedup on `(strategy, semantic_id)`.
 *
 * @see klyde_edge/ui_config/composition/cards/vendors.py — content_key="product_description"
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas / overlay
 */

import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { text } from '@/lib/design/tokens';
import { useCardExport } from '@/lib/design/card-export';
import { cn } from '@/lib/utils/cn';
import { CardShell } from '../card-shell';
import type { ProductExploreResponse } from '@/lib/api/types';

const PRODUCT_EXPLORE_STRATEGY = 'graph_product_explore';

export function ProductDescriptionCard() {
  const { entityId, entityType } = useViewportSelection();
  const enabled = !!entityId && entityType === 'product';

  const { data, isLoading } = useStrategyData<ProductExploreResponse>(
    PRODUCT_EXPLORE_STRATEGY,
    enabled ? { semantic_id: entityId } : {},
    enabled,
  );
  const product = data?.data?.product ?? null;

  // Scene export — overlay (PDP) surface.
  useCardExport(useMemo(() => {
    const description = product?.description_sale ?? product?.description ?? null;
    return {
      label: 'Description',
      content_key: 'product_description',
      surface: 'overlay' as const,
      status: (product ? 'loaded' : enabled ? 'loading' : 'idle') as 'loading' | 'loaded' | 'idle',
      query_key: enabled
        ? ['strategy-data', PRODUCT_EXPLORE_STRATEGY, { semantic_id: entityId }]
        : null,
      text: description ?? 'Description',
      json: { description },
    };
  }, [product, enabled, entityId]));

  if (isLoading && !product) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground gap-2">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading…
      </div>
    );
  }

  const description = product?.description_sale ?? product?.description ?? null;

  if (!description) {
    return (
      <div className={cn(text.muted, 'h-full flex items-center justify-center')}>
        No description on file.
      </div>
    );
  }

  return (
    <CardShell className="h-full overflow-auto p-4">
      <div className={cn(text.eyebrow, 'mb-3')}>Description</div>
      {/* Full-width band; one clean column that fills the band's width
          (not capped narrow, not chopped into newspaper columns). */}
      <p className={cn(text.body, 'leading-relaxed whitespace-pre-wrap')}>
        {description}
      </p>
    </CardShell>
  );
}
